import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';
import rumService from '../../services/api/rum.service';
import errorTrackingService, { type ErrorStats, type ErrorGroup } from '../../services/api/error-tracking.service';

type TabType = 'overview' | 'sessions' | 'performance' | 'network' | 'errors';

// ── Helpers ──────────────────────────────────────────────────────────────────

const VITAL_THRESHOLDS: Record<string, { good: number; poor: number; unit: string; label: string }> = {
  LCP:  { good: 2500, poor: 4000, unit: 'ms', label: 'Largest Contentful Paint' },
  FID:  { good: 100,  poor: 300,  unit: 'ms', label: 'First Input Delay' },
  CLS:  { good: 0.1,  poor: 0.25, unit: '',   label: 'Cumulative Layout Shift' },
  FCP:  { good: 1800, poor: 3000, unit: 'ms', label: 'First Contentful Paint' },
  TTFB: { good: 800,  poor: 1800, unit: 'ms', label: 'Time to First Byte' },
};

function vitalRating(name: string, value: number): 'good' | 'needsImprovement' | 'poor' {
  const t = VITAL_THRESHOLDS[name];
  if (!t) return 'good';
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needsImprovement';
  return 'poor';
}

const RATING_STYLES = {
  good:             { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-600 dark:text-green-400', badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400', ring: 'text-green-500', label: 'Good' },
  needsImprovement: { bg: 'bg-amber-50 dark:bg-amber-900/20',  border: 'border-amber-200 dark:border-amber-800',  text: 'text-amber-600 dark:text-amber-400',  badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',  ring: 'text-amber-500', label: 'Needs Improvement' },
  poor:             { bg: 'bg-red-50 dark:bg-red-900/20',    border: 'border-red-200 dark:border-red-800',    text: 'text-red-600 dark:text-red-400',    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',    ring: 'text-red-500', label: 'Poor' },
};

function VitalGauge({ name, value }: { name: string; value: number }) {
  const threshold = VITAL_THRESHOLDS[name];
  const rating = vitalRating(name, value);
  const style = RATING_STYLES[rating];
  // Gauge: map value to 0-100% arc (cap at 150% of poor threshold)
  const maxVal = threshold ? threshold.poor * 1.5 : 100;
  const pct = Math.min((value / maxVal) * 100, 100);
  const circumference = 2 * Math.PI * 40;
  const dashOffset = circumference - (pct / 100) * circumference * 0.75; // 270° arc

  const fmtValue = name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}`;

  return (
    <div className={`relative rounded-xl border p-5 ${style.bg} ${style.border} transition-all hover:shadow-md`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">{name}</h3>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">{threshold?.label}</p>
        </div>
        <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${style.badge}`}>
          {style.label}
        </span>
      </div>
      <div className="flex items-center space-x-4">
        {/* SVG Gauge */}
        <div className="relative w-20 h-20 shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-[135deg]">
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8"
              className="text-gray-200 dark:text-gray-700" strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`} strokeLinecap="round" />
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8"
              className={style.ring} strokeDasharray={circumference} strokeDashoffset={dashOffset}
              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-lg font-bold ${style.text}`}>{fmtValue}</span>
          </div>
        </div>
        {/* Thresholds */}
        <div className="flex-1 space-y-1 text-[11px]">
          <div className="flex justify-between">
            <span className="text-green-600 dark:text-green-400">Good</span>
            <span className="text-gray-500">&le; {name === 'CLS' ? threshold.good : `${threshold.good}ms`}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-amber-600 dark:text-amber-400">Needs work</span>
            <span className="text-gray-500">&le; {name === 'CLS' ? threshold.poor : `${threshold.poor}ms`}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-red-600 dark:text-red-400">Poor</span>
            <span className="text-gray-500">&gt; {name === 'CLS' ? threshold.poor : `${threshold.poor}ms`}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DistributionBar({ good, needsImprovement, poor }: { good: number; needsImprovement: number; poor: number }) {
  const total = good + needsImprovement + poor;
  if (total === 0) return <div className="h-2.5 rounded-full bg-gray-200 dark:bg-gray-700"></div>;
  const gPct = (good / total) * 100;
  const nPct = (needsImprovement / total) * 100;
  const pPct = (poor / total) * 100;
  return (
    <div className="space-y-1.5">
      <div className="flex rounded-full overflow-hidden h-2.5">
        {gPct > 0 && <div className="bg-green-500 transition-all" style={{ width: `${gPct}%` }}></div>}
        {nPct > 0 && <div className="bg-amber-500 transition-all" style={{ width: `${nPct}%` }}></div>}
        {pPct > 0 && <div className="bg-red-500 transition-all" style={{ width: `${pPct}%` }}></div>}
      </div>
      <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
        <span className="text-green-600">{gPct.toFixed(0)}% good</span>
        <span className="text-amber-600">{nPct.toFixed(0)}% needs work</span>
        <span className="text-red-600">{pPct.toFixed(0)}% poor</span>
      </div>
    </div>
  );
}

// Tab icons as SVG
const TAB_ICONS: Record<string, JSX.Element> = {
  overview: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13h2v8H3zm6-4h2v12H9zm6-6h2v18h-2zm6 10h2v8h-2z" /></svg>,
  sessions: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  performance: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
  network: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>,
  errors: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>,
};

// ── Main Component ───────────────────────────────────────────────────────────

export default function RealUserMonitoringPage() {
  const { t } = useTranslation('rum');
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 24 * 60 * 60 * 1000, endTime: Date.now(), rangeType: 'preset', presetValue: '24h',
  });
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({ enabled: false, interval: 30000 });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // State
  const [overviewStats, setOverviewStats] = useState<any>({ totalSessions: 0, totalPageViews: 0, avgSessionDuration: 0, bounceRate: 0 });
  const [webVitals, setWebVitals] = useState<any>({ lcp: 0, fid: 0, cls: 0, fcp: 0, ttfb: 0 });
  const [sessions, setSessions] = useState<any[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsPageSize] = useState(20);
  const [performanceMetrics, setPerformanceMetrics] = useState<any[]>([]);
  const [performanceDistributions, setPerformanceDistributions] = useState<Record<string, any>>({});
  const [networkRequests, setNetworkRequests] = useState<any[]>([]);
  const [networkStats, setNetworkStats] = useState<any>({ totalRequests: 0, avgDuration: 0, errorRate: 0, totalDataTransferred: 0 });
  const [totalNetworkRequests, setTotalNetworkRequests] = useState(0);
  const [networkPage, setNetworkPage] = useState(1);
  const [networkPageSize] = useState(20);
  const [methodFilter, setMethodFilter] = useState<string>('');
  const [statusCodeFilter, setStatusCodeFilter] = useState<string>('');
  const [errorStats, setErrorStats] = useState<ErrorStats>({ totalErrors: 0, errorGroups: 0, affectedUsers: 0, errorRate: 0, newErrors: 0, regressions: 0, resolvedErrors: 0 });
  const [topErrors, setTopErrors] = useState<ErrorGroup[]>([]);

  useEffect(() => { loadData(); }, [selectedAppId, timeRange, activeTab]);
  useEffect(() => {
    if (activeTab === 'sessions') loadSessions();
    else if (activeTab === 'network') loadNetworkData();
  }, [sessionsPage, networkPage, methodFilter, statusCodeFilter]);

  useEffect(() => {
    if (!autoRefresh.enabled) return;
    const interval = setInterval(() => {
      if (timeRange.rangeType === 'preset' && timeRange.presetValue) {
        const endTime = Date.now();
        const ranges: Record<string, number> = { '15m': 15*60000, '1h': 3600000, '6h': 6*3600000, '12h': 12*3600000, '24h': 86400000, '7d': 7*86400000, '30d': 30*86400000 };
        setTimeRange({ ...timeRange, startTime: endTime - (ranges[timeRange.presetValue] || ranges['24h']), endTime });
      } else { loadData(); }
    }, autoRefresh.interval);
    return () => clearInterval(interval);
  }, [autoRefresh, timeRange, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true); setError(null);
      switch (activeTab) {
        case 'overview': await loadOverviewData(); break;
        case 'sessions': await loadSessions(); break;
        case 'performance': await loadPerformanceData(); break;
        case 'network': await loadNetworkData(); break;
        case 'errors': await loadErrorsData(); break;
      }
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load data');
    } finally { setLoading(false); }
  };

  const loadOverviewData = async () => {
    const stats = await rumService.getOverviewStats(selectedAppId || 'all', new Date(timeRange.startTime).toISOString(), new Date(timeRange.endTime).toISOString());
    const sd = stats.data || stats;
    setOverviewStats({ totalSessions: sd.totalSessions || 0, totalPageViews: sd.pageViews || 0, avgSessionDuration: sd.avgLoadTime || 0, bounceRate: sd.bounceRate || 0, errors: sd.errors || 0 });
    const vitals = await rumService.getWebVitals(selectedAppId || 'all', new Date(timeRange.startTime).toISOString(), new Date(timeRange.endTime).toISOString());
    setWebVitals(vitals.data || vitals);
  };

  const loadSessions = async () => {
    const response = await rumService.getSessions({ applicationId: selectedAppId || 'all', startTime: new Date(timeRange.startTime).toISOString(), endTime: new Date(timeRange.endTime).toISOString(), limit: sessionsPageSize, offset: (sessionsPage - 1) * sessionsPageSize });
    setSessions(response.sessions || []);
    setTotalSessions(response.total || 0);
  };

  const loadPerformanceData = async () => {
    const vitals = await rumService.getWebVitals(selectedAppId || 'all', new Date(timeRange.startTime).toISOString(), new Date(timeRange.endTime).toISOString());
    const metrics = [
      { name: 'LCP', value: vitals.lcp || 0, percentile_50: vitals.lcp_p50 || 0, percentile_75: vitals.lcp_p75 || 0, percentile_95: vitals.lcp_p95 || 0 },
      { name: 'FID', value: vitals.fid || 0, percentile_50: vitals.fid_p50 || 0, percentile_75: vitals.fid_p75 || 0, percentile_95: vitals.fid_p95 || 0 },
      { name: 'CLS', value: vitals.cls || 0, percentile_50: vitals.cls_p50 || 0, percentile_75: vitals.cls_p75 || 0, percentile_95: vitals.cls_p95 || 0 },
      { name: 'FCP', value: vitals.fcp || 0, percentile_50: vitals.fcp_p50 || 0, percentile_75: vitals.fcp_p75 || 0, percentile_95: vitals.fcp_p95 || 0 },
      { name: 'TTFB', value: vitals.ttfb || 0, percentile_50: vitals.ttfb_p50 || 0, percentile_75: vitals.ttfb_p75 || 0, percentile_95: vitals.ttfb_p95 || 0 },
    ];
    setPerformanceMetrics(metrics);
    setPerformanceDistributions({
      LCP: vitals.lcp_distribution || { good: 0, needsImprovement: 0, poor: 0 },
      FID: vitals.fid_distribution || { good: 0, needsImprovement: 0, poor: 0 },
      CLS: vitals.cls_distribution || { good: 0, needsImprovement: 0, poor: 0 },
      FCP: vitals.fcp_distribution || { good: 0, needsImprovement: 0, poor: 0 },
      TTFB: vitals.ttfb_distribution || { good: 0, needsImprovement: 0, poor: 0 },
    });
  };

  const loadNetworkData = async () => {
    const response = await rumService.getNetworkRequests(selectedAppId || 'all', new Date(timeRange.startTime).toISOString(), new Date(timeRange.endTime).toISOString(), { method: methodFilter || undefined, statusCode: statusCodeFilter || undefined, limit: networkPageSize, offset: (networkPage - 1) * networkPageSize });
    setNetworkRequests(response.requests || []);
    setTotalNetworkRequests(response.total || 0);
    setNetworkStats(response.stats || networkStats);
  };

  const loadErrorsData = async () => {
    const response = await errorTrackingService.getOverviewStats(selectedAppId || 'all', new Date(timeRange.startTime).toISOString(), new Date(timeRange.endTime).toISOString());
    setErrorStats(response.stats);
    setTopErrors(response.topErrors || []);
  };

  // Formatters
  const formatDuration = (ms: number) => {
    if (!ms) return '0ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  };
  const formatBytes = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };
  const fmt = (n: number) => { if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toLocaleString(); };
  const fmtTimestamp = (ts: string) => new Date(ts).toLocaleString();

  const getStatusColor = (sc?: number) => {
    if (!sc) return 'text-gray-500';
    if (sc < 300) return 'text-green-600 dark:text-green-400';
    if (sc < 400) return 'text-blue-600 dark:text-blue-400';
    if (sc < 500) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };
  const getMethodBadge = (m: string) => {
    const colors: Record<string, string> = { GET: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', POST: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', PATCH: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', DELETE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    return colors[m] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
  };
  const getErrorStatusBadge = (s: string) => {
    const colors: Record<string, string> = { unresolved: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400', resolved: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400', ignored: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400', in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' };
    return colors[s] || 'bg-gray-100 text-gray-700';
  };
  const getDeviceIcon = (type?: string) => {
    if (type === 'mobile') return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>;
    if (type === 'tablet') return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>;
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
  };

  const sessionsTotalPages = Math.ceil(totalSessions / sessionsPageSize);
  const networkTotalPages = Math.ceil(totalNetworkRequests / networkPageSize);

  const STAT_CARDS = [
    { label: t('overview.totalSessions'), value: fmt(overviewStats.totalSessions || 0), color: 'border-l-cyan-500', icon: <svg className="w-5 h-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
    { label: t('overview.pageViews'), value: fmt(overviewStats.totalPageViews || 0), color: 'border-l-violet-500', icon: <svg className="w-5 h-5 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> },
    { label: t('sessionDetails.sessionInfo.duration'), value: formatDuration(overviewStats.avgSessionDuration || 0), color: 'border-l-emerald-500', icon: <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
    { label: 'Bounce Rate', value: `${(overviewStats.bounceRate || 0).toFixed(1)}%`, color: 'border-l-rose-500', icon: <svg className="w-5 h-5 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg> },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('subtitle')}</p>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <AutoRefreshToggle value={autoRefresh} onChange={setAutoRefresh} onManualRefresh={() => loadData()} lastUpdated={lastUpdated} />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px overflow-x-auto">
              {([
                { id: 'overview', label: t('nav.overview') },
                { id: 'sessions', label: t('nav.sessions') },
                { id: 'performance', label: t('nav.performance') },
                { id: 'network', label: t('nav.network') },
                { id: 'errors', label: t('overview.errors') },
              ] as { id: TabType; label: string }[]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {TAB_ICONS[tab.id]}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button onClick={loadData} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">Retry</button>
          </div>
        ) : (
          <>
            {/* ═══ Overview Tab ═══ */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stat cards with colored left border */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {STAT_CARDS.map(card => (
                    <div key={card.label} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${card.color} p-5`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{card.label}</p>
                        {card.icon}
                      </div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                    </div>
                  ))}
                </div>

                {/* Web Vitals gauges */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('performance.webVitals.title')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    <VitalGauge name="LCP" value={webVitals.lcp || 0} />
                    <VitalGauge name="FID" value={webVitals.fid || 0} />
                    <VitalGauge name="CLS" value={webVitals.cls || 0} />
                    <VitalGauge name="FCP" value={webVitals.fcp || 0} />
                    <VitalGauge name="TTFB" value={webVitals.ttfb || 0} />
                  </div>
                </div>
              </div>
            )}

            {/* ═══ Sessions Tab ═══ */}
            {activeTab === 'sessions' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('sessions.table.sessionId')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('sessions.table.startedAt')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('sessions.table.duration')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('sessions.table.pageViews')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('sessions.table.device')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {sessions.length === 0 ? (
                          <tr><td colSpan={5} className="px-5 py-12 text-center text-gray-500 dark:text-gray-400">{t('sessions.noData')}</td></tr>
                        ) : sessions.map((session: any) => (
                          <tr key={session.id} onClick={() => navigate(`/analytics/rum/sessions/${session.id}`)}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors">
                            <td className="px-5 py-3.5">
                              <span className="text-sm font-mono px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">
                                {session.id.substring(0, 8)}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-sm text-gray-600 dark:text-gray-400">{fmtTimestamp(session.started_at)}</td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center space-x-2">
                                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                  <div className="bg-cyan-500 h-1.5 rounded-full" style={{ width: `${Math.min((session.duration_ms || 0) / 3000, 100)}%` }}></div>
                                </div>
                                <span className="text-sm text-gray-900 dark:text-white font-medium">{formatDuration(session.duration_ms || 0)}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                                {session.page_view_count || 0} pages
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center space-x-1.5 text-sm text-gray-500 dark:text-gray-400">
                                {getDeviceIcon(session.device_type)}
                                <span className="capitalize">{session.device_type || 'unknown'}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {sessions.length > 0 && (
                    <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/30">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('sessions.showing', { from: (sessionsPage - 1) * sessionsPageSize + 1, to: Math.min(sessionsPage * sessionsPageSize, totalSessions), total: totalSessions })}</span>
                      <div className="flex items-center space-x-1">
                        <button onClick={() => setSessionsPage(p => Math.max(1, p - 1))} disabled={sessionsPage === 1}
                          className="px-3 py-1.5 text-sm rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors">&larr; {t('sessions.previous')}</button>
                        <span className="px-3 py-1.5 text-sm text-gray-500">{sessionsPage} / {sessionsTotalPages}</span>
                        <button onClick={() => setSessionsPage(p => Math.min(sessionsTotalPages, p + 1))} disabled={sessionsPage >= sessionsTotalPages}
                          className="px-3 py-1.5 text-sm rounded-lg text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 disabled:opacity-40 transition-colors">{t('sessions.next')} &rarr;</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ═══ Performance Tab ═══ */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {performanceMetrics.map((metric) => {
                    const rating = vitalRating(metric.name, metric.value);
                    const style = RATING_STYLES[rating];
                    const fmtVal = (v: number) => metric.name === 'CLS' ? v.toFixed(3) : `${Math.round(v)}ms`;
                    const dist = performanceDistributions[metric.name] || { good: 0, needsImprovement: 0, poor: 0 };
                    return (
                      <div key={metric.name} className={`rounded-xl border p-5 ${style.bg} ${style.border} transition-all hover:shadow-md`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{metric.name}</h3>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400">{VITAL_THRESHOLDS[metric.name]?.label}</p>
                          </div>
                          <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${style.badge}`}>{style.label}</span>
                        </div>
                        <p className={`text-3xl font-bold mb-4 ${style.text}`}>{fmtVal(metric.value)}</p>

                        {/* Distribution bar */}
                        <DistributionBar good={dist.good} needsImprovement={dist.needsImprovement} poor={dist.poor} />

                        {/* Percentiles */}
                        <div className="mt-4 grid grid-cols-3 gap-2">
                          {(['50', '75', '95'] as const).map(p => (
                            <div key={p} className="text-center p-2 rounded-lg bg-white/50 dark:bg-gray-800/50">
                              <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">P{p}</p>
                              <p className="text-sm font-bold text-gray-900 dark:text-white">{fmtVal(metric[`percentile_${p}`])}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ═══ Network Tab ═══ */}
            {activeTab === 'network' && (
              <div className="space-y-4">
                {/* Network stat cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: t('network.stats.totalRequests'), value: fmt(networkStats.totalRequests || 0), color: 'border-l-cyan-500', icon: <svg className="w-5 h-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg> },
                    { label: t('network.stats.avgDuration'), value: `${Math.round(networkStats.avgDuration || 0)}ms`, color: 'border-l-violet-500', icon: <svg className="w-5 h-5 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
                    { label: t('network.stats.errorRate'), value: `${(networkStats.errorRate || 0).toFixed(1)}%`, color: 'border-l-rose-500', icon: <svg className="w-5 h-5 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg> },
                    { label: t('network.stats.totalDataTransferred'), value: formatBytes(networkStats.totalDataTransferred), color: 'border-l-emerald-500', icon: <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg> },
                  ].map(card => (
                    <div key={card.label} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${card.color} p-5`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{card.label}</p>
                        {card.icon}
                      </div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                    </div>
                  ))}
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('network.filters.method')}</label>
                      <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm dark:text-gray-300">
                        <option value="">{t('network.filters.allMethods')}</option>
                        <option>GET</option><option>POST</option><option>PUT</option><option>DELETE</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('network.filters.statusCode')}</label>
                      <select value={statusCodeFilter} onChange={(e) => setStatusCodeFilter(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm dark:text-gray-300">
                        <option value="">{t('network.filters.allStatuses')}</option>
                        <option value="2xx">{t('network.filters.successful')}</option>
                        <option value="4xx">{t('network.filters.clientError')}</option>
                        <option value="5xx">{t('network.filters.serverError')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Network table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('network.table.method')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('network.table.url')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('network.table.statusCode')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('network.table.duration')}</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('network.table.size')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {networkRequests.length === 0 ? (
                          <tr><td colSpan={5} className="px-5 py-12 text-center text-gray-500 dark:text-gray-400">{t('network.noData')}</td></tr>
                        ) : networkRequests.map((req: any) => (
                          <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="px-5 py-3"><span className={`px-2.5 py-0.5 rounded-md text-xs font-bold ${getMethodBadge(req.method)}`}>{req.method}</span></td>
                            <td className="px-5 py-3 max-w-md"><div className="truncate text-sm font-mono text-gray-900 dark:text-white" title={req.url}>{req.url}</div></td>
                            <td className="px-5 py-3"><span className={`text-sm font-bold ${getStatusColor(req.status_code)}`}>{req.status_code || '-'}</span></td>
                            <td className="px-5 py-3">
                              <div className="flex items-center space-x-2">
                                <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                  <div className={`h-1.5 rounded-full ${(req.duration_ms || 0) > 1000 ? 'bg-red-500' : (req.duration_ms || 0) > 300 ? 'bg-amber-500' : 'bg-green-500'}`}
                                    style={{ width: `${Math.min(((req.duration_ms || 0) / 2000) * 100, 100)}%` }}></div>
                                </div>
                                <span className="text-sm text-gray-900 dark:text-white">{req.duration_ms}ms</span>
                              </div>
                            </td>
                            <td className="px-5 py-3 text-sm text-gray-500">{formatBytes(req.response_size)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {networkRequests.length > 0 && (
                    <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/30">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('network.showing', { from: (networkPage - 1) * networkPageSize + 1, to: Math.min(networkPage * networkPageSize, totalNetworkRequests), total: totalNetworkRequests })}</span>
                      <div className="flex items-center space-x-1">
                        <button onClick={() => setNetworkPage(p => Math.max(1, p - 1))} disabled={networkPage === 1}
                          className="px-3 py-1.5 text-sm rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors">&larr; {t('network.previous')}</button>
                        <span className="px-3 py-1.5 text-sm text-gray-500">{networkPage} / {networkTotalPages}</span>
                        <button onClick={() => setNetworkPage(p => Math.min(networkTotalPages, p + 1))} disabled={networkPage >= networkTotalPages}
                          className="px-3 py-1.5 text-sm rounded-lg text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 disabled:opacity-40 transition-colors">{t('network.next')} &rarr;</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ═══ Errors Tab ═══ */}
            {activeTab === 'errors' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: t('overview.errors'), value: fmt(errorStats.totalErrors), color: 'border-l-red-500', icon: <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
                    { label: 'Error Groups', value: fmt(errorStats.errorGroups), color: 'border-l-amber-500', icon: <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg> },
                    { label: 'Affected Users', value: fmt(errorStats.affectedUsers), color: 'border-l-violet-500', icon: <svg className="w-5 h-5 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg> },
                    { label: t('network.stats.errorRate'), value: `${errorStats.errorRate.toFixed(1)}%`, color: 'border-l-rose-500', icon: <svg className="w-5 h-5 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg> },
                  ].map(card => (
                    <div key={card.label} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${card.color} p-5`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{card.label}</p>
                        {card.icon}
                      </div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                    </div>
                  ))}
                </div>

                {/* Top Errors */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Top Errors</h2>
                  </div>
                  {topErrors.length === 0 ? (
                    <div className="px-5 py-12 text-center text-gray-500 dark:text-gray-400">{t('common.noData')}</div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {topErrors.map((err, idx) => {
                        const maxOcc = topErrors[0]?.occurrence_count || 1;
                        return (
                          <div key={err.id} onClick={() => navigate(`/analytics/rum/errors/${err.id}`)}
                            className="px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors">
                            <div className="flex items-start space-x-3">
                              <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 shrink-0 mt-0.5">{idx + 1}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-sm font-bold text-gray-900 dark:text-white">{err.error_type}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getErrorStatusBadge(err.status)}`}>{err.status}</span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate mb-2">{err.message}</p>
                                <div className="flex items-center space-x-4">
                                  <div className="flex-1">
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                      <div className="bg-red-500 h-1.5 rounded-full" style={{ width: `${(err.occurrence_count / maxOcc) * 100}%` }}></div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-gray-500 shrink-0">{err.occurrence_count} occurrences</span>
                                  <span className="text-xs text-gray-500 shrink-0">{err.user_count} users</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
